/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IDobbiesFlipbookViewProps } from './dobbies-flipbook';

type FullscreenCapableElement = HTMLElement & {
    webkitRequestFullscreen?: () => void;
    msRequestFullscreen?: () => void;
};

export default (props: IDobbiesFlipbookViewProps) => {
    const {
        flipbookUrl,
        title,
        description,
        showTitle,
        showDescription,
        ctaText,
        showCta,
        desktopAspectRatio,
        mobileAspectRatio,
        maxWidth,
        backgroundColor,
        enableFullscreen
    } = props.config;

    const viewerRef = React.useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = React.useState(false);

    React.useEffect(() => {
        const handleFullscreenChange = (): void => {
            setIsFullscreen(document.fullscreenElement === viewerRef.current);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    if (!flipbookUrl) {
        return null;
    }

    const style = {
        '--flipbook-desktop-ratio': desktopAspectRatio || '16 / 10',
        '--flipbook-mobile-ratio': mobileAspectRatio || '4 / 5',
        '--flipbook-max-width': `${maxWidth || 1400}px`,
        '--flipbook-background': backgroundColor || '#ffffff'
    } as React.CSSProperties;

    const enterFullscreen = async (): Promise<void> => {
        if (!enableFullscreen || !viewerRef.current) {
            return;
        }

        const element = viewerRef.current as FullscreenCapableElement;

        try {
            if (typeof element.requestFullscreen === 'function') {
                await element.requestFullscreen();
                return;
            }

            if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
                return;
            }

            if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
        } catch (error) {
            console.error('Unable to enter fullscreen mode.', error);
        }
    };

    const exitFullscreen = async (): Promise<void> => {
        try {
            if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
                await document.exitFullscreen();
            }
        } catch (error) {
            console.error('Unable to exit fullscreen mode.', error);
        }
    };

    const handleCtaClick = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();
        await enterFullscreen();
    };

    return (
        <section className='flipbook-module' style={style} aria-labelledby={title ? 'flipbook-module-title' : undefined}>
            <div className='flipbook-module__container'>
                <div className='flipbook-module__content'>
                    {showTitle !== false && title && (
                        <h2 id='flipbook-module-title' className='flipbook-module__title'>
                            {title}
                        </h2>
                    )}

                    {showDescription !== false && description && <p className='flipbook-module__description'>{description}</p>}

                    {showCta !== false && enableFullscreen && ctaText && (
                        <button
                            type='button'
                            className='flipbook-module__cta'
                            onClick={handleCtaClick}
                            aria-label={`Open ${title || 'flipbook'} in fullscreen`}
                        >
                            {ctaText}
                        </button>
                    )}
                </div>

                <div className='flipbook-module__viewer-column'>
                    <div
                        ref={viewerRef}
                        className={`flipbook-module__viewer${isFullscreen ? ' flipbook-module__viewer--fullscreen' : ''}`}
                        role='region'
                        aria-label={title || 'Flipbook viewer'}
                    >
                        <iframe
                            className='flipbook-module__iframe'
                            src={flipbookUrl}
                            title={title || 'Flipbook viewer'}
                            allow='fullscreen'
                            allowFullScreen={enableFullscreen !== false}
                            referrerPolicy='strict-origin-when-cross-origin'
                        />

                        {isFullscreen && (
                            <button
                                type='button'
                                className='flipbook-module__exit-fullscreen'
                                onClick={exitFullscreen}
                                aria-label='Exit fullscreen'
                            >
                                Exit fullscreen
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
